import type { FC } from "react";
import { Link } from "react-router";
import { normalizeOrderCode } from "../../../../libs/mypage/orderCode";
import type { OrderDetailDTO } from "../../components/format";
import { SectionHeading } from "../../components/SectionHeading";

export const ActionsSection: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const isShipped = order.status === "SHIPPED" || order.status === "PARTLY_SHIPPED";
  const isCancelledOrRefunded = order.status === "CANCELLED" || order.status === "REFUNDED";
  const changeUnavailableNote = isCancelledOrRefunded
    ? "キャンセル済みです"
    : isShipped
      ? "出荷済みのため変更できません"
      : order.status === "WORKING"
        ? "出荷作業中のため変更できません"
        : "現在変更できません";
  const addressUnavailableNote = isCancelledOrRefunded
    ? "キャンセル済みです"
    : isShipped
      ? "出荷済みのため住所変更はできません"
      : order.status === "WORKING"
        ? "出荷作業中のため住所変更できません"
        : "現在お届け先を変更できません";
  const slug = normalizeOrderCode(order.code);

  // 返品行の表示分岐:
  //  - キャンセル済 → disabled (「キャンセル済みです」)
  //  - active ReturnRequest あり → disabled (「申請中」)。ActiveReturnSection 側で詳細展開済み
  //  - 出荷済 (isShipped) → eligibility いかんに関わらず /return リンクを生かす。
  //    両 track ineligible でも、ウィザードの診断ステップで個別の理由を見せたいので入口は開ける
  //  - 未出荷 → disabled (「発送後に〜」)
  const returnRow = (() => {
    if (isCancelledOrRefunded) {
      return (
        <ActionRow
          label="返品・交換を申請"
          available={false}
          unavailableNote="キャンセル済みです"
        />
      );
    }
    if (order.activeReturnRequest) {
      // 返金 / 交換まで終わった申請も activeReturnRequest に載る (完了を見せるため)。
      // 「申請中」と書くとまだ手続きが動いているように読めるので、完了は言い分ける。
      const { code, phase } = order.activeReturnRequest;
      const state = phase === "completed" ? "手続き完了" : "申請中";
      return (
        <ActionRow
          label="返品・交換を申請"
          available={false}
          unavailableNote={code ? `${state} (${code})` : state}
        />
      );
    }
    if (isShipped) {
      return <ActionRow label="返品・交換を申請" available={true} to={`/orders/${slug}/return`} />;
    }
    return (
      <ActionRow
        label="返品・交換を申請"
        available={false}
        unavailableNote="発送後にお申し込みいただけます"
      />
    );
  })();

  return (
    <section className="mb-10">
      <SectionHeading title="この注文について" />
      <ul className="divide-y divide-neutral-200">
        <ActionRow
          label="領収書を表示"
          available={!isCancelledOrRefunded}
          to={`/orders/${slug}/receipt`}
          unavailableNote={isCancelledOrRefunded ? "キャンセル済みのため発行できません" : undefined}
        />
        <ActionRow
          label="注文内容の変更"
          available={order.isOrderChangeable}
          to={`/orders/${slug}/change`}
          unavailableNote={changeUnavailableNote}
        />
        <ActionRow
          label="お届け先を変更"
          available={order.isAddressEditable}
          to={`/orders/${slug}/address`}
          unavailableNote={addressUnavailableNote}
        />
        {returnRow}
      </ul>
    </section>
  );
};

const ActionRow: FC<{
  label: string;
  available: boolean;
  /** 外部 URL (Shopify Order Status 等)。新規タブで開く */
  href?: string;
  external?: boolean;
  /** 内部 react-router の遷移先 (cancel/address フォーム等) */
  to?: string;
  unavailableNote?: string;
  comingSoonNote?: string;
}> = ({ label, available, href, external, to, unavailableNote, comingSoonNote }) => {
  if (!available) {
    return (
      <li className="flex items-baseline justify-between gap-3 py-4 text-sm">
        <span className="text-neutral-400">{label}</span>
        {unavailableNote && <span className="text-neutral-400 text-xs">{unavailableNote}</span>}
      </li>
    );
  }

  if (to) {
    return (
      <li className="py-4">
        <Link
          to={to}
          className="flex items-baseline justify-between gap-3 text-neutral-900 text-sm hover:underline"
        >
          <span>{label}</span>
          <span className="text-neutral-500 text-xs">→</span>
        </Link>
      </li>
    );
  }

  if (href) {
    return (
      <li className="py-4">
        <a
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
          className="flex items-baseline justify-between gap-3 text-neutral-900 text-sm hover:underline"
        >
          <span>{label}</span>
          <span className="text-neutral-500 text-xs">{external ? "↗" : "→"}</span>
        </a>
      </li>
    );
  }

  // available だが遷移先を持たないもの
  return (
    <li className="flex items-baseline justify-between gap-3 py-4 text-sm">
      <span className="text-neutral-700">{label}</span>
      {comingSoonNote && <span className="text-neutral-400 text-xs">{comingSoonNote}</span>}
    </li>
  );
};
